import React, { useContext } from 'react';
import './FoodDisplay.css';
import { useTranslation } from 'react-i18next';
import FoodItem from '../FoodItem/FoodItem';
import { StoreContext } from '../../Context/StoreContext';
import FoodShimmer from '../common/FoodShimmer';

// Utility function to ensure consistent string keys for React
const getStringKey = (id, fallback = '') => {
    if (!id) return fallback;
    if (typeof id === 'string') return id;
    
    // Handle populated MongoDB documents
    if (typeof id === 'object' && id._id) {
        return id._id.toString();
    }
    
    if (id.toString && typeof id.toString === 'function') return id.toString();
    return String(id) || fallback;
};

const FoodDisplay = ({ category, restaurantId, searchQuery: localSearchQuery }) => {
  const { t } = useTranslation();
  const { food_list, searchQuery: globalSearchQuery, selectedRestaurant, isFoodLoading } = useContext(StoreContext);

  // Use local search query if provided (for restaurant-specific search), otherwise use global
  const activeSearchQuery = localSearchQuery !== undefined ? localSearchQuery : globalSearchQuery;

  // Check restaurant operational status
  const getRestaurantStatus = (restaurant) => {
    if (!restaurant) return { isOpen: true };
    
    // Check for manual override first
    if (restaurant.operationalStatus?.override && restaurant.operationalStatus.override !== 'auto') {
      return { isOpen: restaurant.operationalStatus.override === 'open' };
    }

    // Fall back to automatic status based on business hours
    if (!restaurant.businessHours || restaurant.businessHours.length === 0) {
      return { isOpen: true };
    }

    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const todayHours = restaurant.businessHours.find(hours => hours.day === currentDay);
    
    if (!todayHours) {
      return { isOpen: false };
    }

    const [openHour, openMin] = todayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = todayHours.close.split(':').map(Number);
    
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;

    return { isOpen: !(currentTime < openTime || currentTime > closeTime) };
  };

  const restaurantStatus = getRestaurantStatus(selectedRestaurant);

  // Filter food items based on category, search query, and restaurant
  const filteredFoodList = food_list.filter((item) => {
    // Skip invalid items
    if (!item || !item.name) return false;
    
    // Filter by restaurant if restaurantId is provided
    if (restaurantId && item.restaurant !== restaurantId) {
      return false;
    }
    
    // If showing all items, only apply search and restaurant filtering
    if (category === "All") {
      return activeSearchQuery === "" || 
        item.name.toLowerCase().includes(activeSearchQuery.toLowerCase()) || 
        (item.description && item.description.toLowerCase().includes(activeSearchQuery.toLowerCase()));
    }
    
    // Case-insensitive comparison and trim spaces for more reliable matching
    const categoryLower = category.toLowerCase().trim();
    
    // Check for category match - prioritize the populated menu data structure
    let matchesCategory = false;
    
    // First check the populated menu structure (most common case)
    if (item.menu && typeof item.menu === 'object') {
      // Check menu.name (this is the category name displayed in the UI)
      if (item.menu.name) {
        matchesCategory = item.menu.name.toLowerCase().trim() === categoryLower;
      }
      
      // Also check menu.category as fallback
      if (!matchesCategory && item.menu.category) {
        matchesCategory = item.menu.category.toLowerCase().trim() === categoryLower;
      }
    }
    
    // Fallback: check direct category field (legacy support)
    if (!matchesCategory && item.category) {
      matchesCategory = item.category.toLowerCase().trim() === categoryLower;
    }
    
    // Fallback: check categoryName field
    if (!matchesCategory && item.categoryName) {
      matchesCategory = item.categoryName.toLowerCase().trim() === categoryLower;
    }
    
    // Fallback: check categories array if present
    if (!matchesCategory && item.categories) {
      if (Array.isArray(item.categories)) {
        matchesCategory = item.categories.some(cat => {
          if (typeof cat === 'string') {
            return cat.toLowerCase().trim() === categoryLower;
          } else if (cat && typeof cat === 'object' && cat.name) {
            return cat.name.toLowerCase().trim() === categoryLower;
          }
          return false;
        });
      } else if (typeof item.categories === 'string') {
        matchesCategory = item.categories.toLowerCase().trim() === categoryLower;
      }
    }
    
    // Check for partial match as final fallback - less strict matching
    if (!matchesCategory) {
      // Try populated menu name for partial match
      if (item.menu && item.menu.name) {
        const menuName = item.menu.name.toLowerCase().trim();
        matchesCategory = menuName.includes(categoryLower) || categoryLower.includes(menuName);
      }
      
      // Try populated menu category for partial match
      if (!matchesCategory && item.menu && item.menu.category) {
        const menuCategory = item.menu.category.toLowerCase().trim();
        matchesCategory = menuCategory.includes(categoryLower) || categoryLower.includes(menuCategory);
      }
      
      // Try direct category for partial match
      if (!matchesCategory && item.category) {
        const itemCategory = item.category.toLowerCase().trim();
        matchesCategory = itemCategory.includes(categoryLower) || categoryLower.includes(itemCategory);
      }
    }
      
    const matchesSearch = activeSearchQuery === "" || 
      item.name.toLowerCase().includes(activeSearchQuery.toLowerCase()) || 
      (item.description && item.description.toLowerCase().includes(activeSearchQuery.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  // Display message when no results are found (only when not loading)
  const noResultsFound = !isFoodLoading && filteredFoodList.length === 0;

  // Determine the title based on whether a category is selected and if we're on a restaurant page
  const getTitle = () => {
    if (restaurantId) {
      // We're on a restaurant-specific page
      if (category !== "All") {
        return `${category}`;
      } else if (activeSearchQuery) {
        return `Search Results`;
      } else {
        return `Menu`;
      }
    } else {
      // We're on the general home page
      return category !== "All" 
        ? `${category}` 
        : selectedRestaurant 
          ? `${selectedRestaurant.name} Menu` 
          : "All Items";
    }
  };

  return (
    <div className='food-display' id='food-display'>
      <h2>{getTitle()}</h2>
      
      {isFoodLoading ? (
        <div className='food-display-list'>
          {Array.from({ length: 8 }, (_, index) => (
            <FoodShimmer key={`shimmer-${index}`} />
          ))}
        </div>
      ) : noResultsFound ? (
        <div className="no-results">
          <p>Please Refresh the page!</p>
          {activeSearchQuery && <p>Try a different search term or browse all categories.</p>}
          {category !== "All" && (
            <p>No items found! Try selecting a different category or refresh the page.</p>
          )}
        </div>
      ) : (
        <div className='food-display-list'>
          {filteredFoodList.map((item, index) => {
            // Get restaurant name from the item's restaurant data
            let restaurantName = '';
            if (restaurantId && selectedRestaurant) {
              // On restaurant-specific page, use selected restaurant name
              restaurantName = selectedRestaurant.name;
            } else if (item.restaurant && typeof item.restaurant === 'object' && item.restaurant.name) {
              // On home page, use populated restaurant name
              restaurantName = item.restaurant.name;
            } else if (item.restaurantName) {
              // Fallback to direct restaurant name field
              restaurantName = item.restaurantName;
            }

            // Get restaurantId for this item
            let itemRestaurantId = '';
            if (restaurantId) {
              // On restaurant-specific page, use the provided restaurantId
              itemRestaurantId = restaurantId;
            } else if (item.restaurant && typeof item.restaurant === 'object' && item.restaurant._id) {
              // On home page, use populated restaurant ID
              itemRestaurantId = item.restaurant._id;
            } else if (item.restaurant && typeof item.restaurant === 'string') {
              // If restaurant is just an ID string
              itemRestaurantId = item.restaurant;
            }

            return (
              <FoodItem
                key={getStringKey(item._id, `food-${index}`)}
                id={getStringKey(item._id, `food-${index}`)}
                name={item.name || 'Unnamed Item'}
                image={item.image || null}
                desc={item.description || ''}
                price={typeof item.price === 'number' ? item.price : 0}
                restaurantName={restaurantName}
                restaurantId={itemRestaurantId}
                isAvailable={item.isAvailable !== false}
                restaurantStatus={restaurantStatus}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FoodDisplay;